// frontend/src/lib/identityGoogle.js
// ✅ FIXED: getIdentityHeaders now refuses to attach expired tokens.
//    Expired token => no Authorization header + "auth:expired" event (debounced),
//    so useAuth can sign the user out cleanly instead of the backend
//    rejecting stale kids with "No pem found for envelope".

const AUTH_KEY = "artibytes_auth_v1";
const TOKEN_KEY = "artibytes_id_token";

// Consider a token dead 60s before actual exp to absorb clock skew.
const EXPIRY_SKEW_MS = 60 * 1000;

/* ----------------------------- Token helpers ----------------------------- */

function decodeJwtPayload(token) {
  const parts = String(token || "").split(".");
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function getIdToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

export function isTokenExpired(token = null) {
  const t = token || getIdToken();
  if (!t) return true;
  const payload = decodeJwtPayload(t);
  if (!payload?.exp) return true;
  return payload.exp * 1000 - Date.now() < EXPIRY_SKEW_MS;
}

/**
 * Returns the stored token ONLY if it is still valid, else null.
 */
export function getValidToken() {
  const t = getIdToken();
  if (!t) return null;
  return isTokenExpired(t) ? null : t;
}

/* -------------------------- Expiry notification -------------------------- */

let lastExpiredEventAt = 0;

function notifyExpiredOnce() {
  // Many components build headers per-request; debounce so we
  // dispatch at most one event per 5s.
  const now = Date.now();
  if (now - lastExpiredEventAt < 5000) return;
  lastExpiredEventAt = now;
  try {
    window.dispatchEvent(new CustomEvent("auth:expired"));
  } catch {}
}

/* ------------------------------ Public API ------------------------------- */

/**
 * ✅ THE FIX: only ever attach a *valid* Bearer token.
 * No more sending day-old tokens the backend cannot verify.
 * (x-google-id / x-google-email headers removed: spoofable and unused server-side.)
 */
export function getIdentityHeaders() {
  const token = getIdToken();
  if (!token) return {};

  if (isTokenExpired(token)) {
    notifyExpiredOnce();
    return {};
  }

  return { Authorization: `Bearer ${token}` };
}

/**
 * Kept for existing imports (Create.jsx, Gallery.jsx, Result.jsx, ...).
 * Now: expired => notify + false. It NO LONGER silently wipes storage;
 * useAuth owns sign-out so the redirect/UX is consistent.
 */
export async function refreshTokenIfNeeded() {
  if (isTokenExpired()) {
    console.log("[IdentityGoogle] Token expired — requesting re-auth");
    notifyExpiredOnce();
    return false;
  }
  return true;
}

export function isAuthenticated() {
  return !!getValidToken();
}

export function getCurrentUser() {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    return JSON.parse(raw)?.user || null;
  } catch {
    return null;
  }
}

/** Kept for existing imports across pages. */
export async function waitForIdentity() {
  return { headers: getIdentityHeaders(), user: getCurrentUser() };
}

/* ------------------------- Storage write/clear --------------------------- */
/* Single source of truth for the storage shape: { user, idToken, refreshedAt } */

export function persistAuth(user, idToken) {
  localStorage.setItem(
    AUTH_KEY,
    JSON.stringify({ user, idToken, refreshedAt: Date.now() })
  );
  localStorage.setItem(TOKEN_KEY, idToken);
}

export function clearAuth() {
  try {
    localStorage.removeItem(AUTH_KEY);
    localStorage.removeItem(TOKEN_KEY);
  } catch {}
}

/** Kept so any legacy import of signOutGoogle still works. */
export async function signOutGoogle() {
  clearAuth();
}